import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { showSuccess, showError } from '../utils/toast';
import './UserModal.css';

const UserModal = ({ user, onClose }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    email: ''
  });
  const [avatarDetails, setAvatarDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      // Check if user object already has the data we need
      if (user.first_name && user.email) {
        // User data is already available, use it directly
        setFormData({
          first_name: user.first_name || user.name || '',
          email: user.email || ''
        });
        // Set avatar details
        if (user.avatar_details && Array.isArray(user.avatar_details)) {
          setAvatarDetails(user.avatar_details);
        }
      } else if (user.id || user.user_id) {
        // Fetch full user details if we only have ID
        fetchUserDetails(user.id || user.user_id);
      } else {
        // Fallback: try to use whatever data is available
        setFormData({
          first_name: user.first_name || user.name || '',
          email: user.email || ''
        });
        if (user.avatar_details && Array.isArray(user.avatar_details)) {
          setAvatarDetails(user.avatar_details);
        }
      }
    }
  }, [user]);

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiClient.get(`/admin/users/${userId}`, token);
      
      console.log('User details API response:', response);
      
      // Handle API response structure:
      // { status: true, status_code: 200, data: { ...user data... }, error: null, msg: "..." }
      // OR direct user object
      const userData = response.data || response;
      
      setFormData({
        first_name: userData.first_name || userData.name || '',
        email: userData.email || ''
      });
      
      // Set avatar details
      if (userData.avatar_details && Array.isArray(userData.avatar_details)) {
        setAvatarDetails(userData.avatar_details);
      }
      
      console.log('Form data set:', {
        first_name: userData.first_name || userData.name || '',
        email: userData.email || '',
        avatarDetails: userData.avatar_details
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      const errorMsg = 'Failed to load user details: ' + (error.message || 'Unknown error');
      setError(errorMsg);
      showError(errorMsg);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (user) {
        // Update existing user
        const userId = user.id || user.user_id;
        
        // Prepare update data (only include fields that can be updated)
        const updateData = {
          first_name: formData.first_name,
          email: formData.email
        };
        
        // Update user details
        try {
          await apiClient.put(`/admin/users/${userId}`, updateData, token);
          console.log('✅ User details updated successfully');
          showSuccess('User updated successfully');
        } catch (updateError) {
          console.error('Error updating user details:', updateError);
          const errorMsg = 'Failed to update user details: ' + (updateError.message || updateError.error || 'Unknown error');
          showError(errorMsg);
          throw new Error(errorMsg);
        }
      } else {
        // Note: API doesn't seem to have a create endpoint, so we'll show an error
        showError('Creating new users is not supported by this API');
        setLoading(false);
        return;
      }

      onClose(true); // Pass true to indicate successful save, so page should refresh
    } catch (err) {
      const errorMsg = err.error || err.message || 'Failed to save user';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{user ? 'Edit User' : 'Add New User'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Avatar Details</label>
            {avatarDetails.length > 0 ? (
              <div className="avatar-details-display">
                {avatarDetails.map((avatar, index) => (
                  <div key={index} className="avatar-item">
                    <span className="avatar-name">
                      {`${avatar.first_name || ''} ${avatar.last_name || ''}`.trim() || avatar.avatar_type || 'Avatar'}
                    </span>
                    <span className={`avatar-status ${avatar.status || 'active'}`}>
                      {avatar.status || 'active'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-avatar-display">No Avatar</div>
            )}
          </div>

          {!user && (
            <div className="info-message">
              Note: Creating new users is not supported by this API. Please use the external system to create users.
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? 'Saving...' : user ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;



